import React from 'react';
import PropTypes from 'prop-types';
import { Transition as ReactTransition } from 'react-transition-group';

import Browser from '@wetransfer/concorde-browser';

import Transition from './transition';

import Actions from '@app/store/action';
import Store from '@app/store';

// The Transfer component is actually one of the following components,
// but which one gets decided in this file.
import TermsConditions from '@component/transfer/terms-conditions';
import Downloader from '@component/transfer/downloader';
import Uploader from '@component/transfer/uploader';
import Bubble from '@component/transfer/bubble';

export default class Transfer extends React.Component {
  static propTypes = {
    route: PropTypes.object,
  };

  state = {
    mode: false,
  };

  // The render method decides what to render based on the transfer mode.
  // There are cases where we need to set the transfer mode before rendering
  // using the props. For example, when we download a transfer and then we
  // want to send another one, and we navigate from `/downloads/:id/:secret`
  // to `/` (the `:id` (transferId) and `:secret` come from the props). So we do
  // that here in the `getDerivedStateFromProps`.
  static getDerivedStateFromProps(props, state) {
    return { ...state, mode: Transfer.getTransferMode(props) };
  }

  static getTransferMode(props) {
    const { session, transfer } = Store.getState();
    let { id, secret } = props;
    if (Browser.isMobile) {
      id = props.route.params.id;
      secret = props.route.params.secret;
    }
    // Deciding between uploader and downloader is a pretty simple thing,
    // but if the Terms & Conditions are not yet accepted we really need
    // to force another controller until they are accepted.
    // If parent_id is present, it means that it's a forwarded transfer <-- upload
    let transferMode =
      id && secret && !transfer.parent_id ? 'download' : 'upload';

    // User needs to accept the terms and conditions.
    if (!session.tandc && Browser.isMobile) {
      transferMode = 'tandc';
    }
    return transferMode;
  }

  constructor(props) {
    super(props);
    this.onEnter = this.onEnter.bind(this);
  }

  showTransfer() {
    Store.dispatch(Actions.Layout.showUI());
  }

  // Based on props we will decide what mode we are currently in
  setCurrentMode(props, forceUpdate = false) {
    const transferMode = Transfer.getTransferMode(props);

    // Avoid overwritten current session if mode is identical
    const needsUpdate = this.state.mode !== transferMode;

    if (forceUpdate === false && !needsUpdate) {
      return;
    }

    this.setState({ mode: transferMode });
  }

  // When the component appears
  onEnter(component) {
    new Transition({
      prevType: false,
      newType:
        this.props.route.type === 'transfer' ? false : this.props.route.type,
      component,
      animate: false, // We don't need to animate this
    });
  }

  // When component is updated
  componentDidUpdate(prevProps) {
    this.setCurrentMode(this.props);
    new Transition({
      prevType:
        prevProps.route.type === 'transfer' ? false : prevProps.route.type,
      newType:
        this.props.route.type === 'transfer' ? false : this.props.route.type,
      component: this.node,
    });

    // Should we clear any notifications? (When panel opens)
    if (
      prevProps.route.type === 'transfer' &&
      this.props.route.type !== 'transfer'
    ) {
      Store.dispatch(Actions.Notification.clearTransfer());
    }
  }

  // So this is a pretty unusual setup for React, but it makes sense trust us.
  render() {
    let component = null;
    let clickBlock = null;
    const { notification, layout } = Store.getState();
    const props =
      Browser.isMobile && this.props.route.params.id
        ? this.props.route.params
        : this.props;
    const classNames = ['transfer'];

    if (layout.hidden === true) {
      classNames.push('transfer--html5-fullscreen');
      clickBlock = (
        <div className="transfer__block-click" onClick={this.showTransfer} />
      );
    }

    switch (this.state.mode) {
      case 'upload':
        component = <Uploader {...props} />;
        break;

      case 'download':
        component = <Downloader {...props} />;
        break;

      case 'tandc':
        component = <TermsConditions {...props} />;
        break;
    }

    const bubble = !notification.transfer.text ? null : (
      <Bubble
        parent={this.node}
        notification={notification.transfer}
        type="transfer"
      />
    );

    return (
      <ReactTransition
        {...this.props}
        timeout={{ in: 0, out: 1000 }}
        onEnter={this.onEnter}
      >
        <div
          className={classNames.join(' ')}
          ref={(node) => (this.node = node)}
        >
          {component}
          {bubble}
          {clickBlock}
        </div>
      </ReactTransition>
    );
  }
}
